<template>
  <el-dialog
    v-model="visible"
    title="결재 요청"
    width="650px"
    append-to-body
    @open="loadSuggestedApprovers"
  >
    <el-form :model="form" label-width="80px">
      <el-form-item label="제목">
        <el-input v-model="form.title" placeholder="결재 제목 (비워두면 자동 생성)" />
      </el-form-item>
      <el-form-item label="메모">
        <el-input v-model="form.memo" type="textarea" :rows="2" placeholder="기안 메모 (선택)" />
      </el-form-item>

      <!-- 결재선 (APPROVAL) -->
      <el-divider content-position="left">결재선</el-divider>

      <div v-if="loadingApprovers" style="text-align: center; padding: 20px;">
        <el-icon class="is-loading"><Loading /></el-icon> 결재선 불러오는 중...
      </div>

      <template v-else>
        <el-table :data="form.approvalSteps" border size="small" style="margin-bottom: 10px;">
          <el-table-column prop="sequence" label="순서" width="60" align="center" />
          <el-table-column prop="step_name" label="단계명" width="120">
            <template #default="{ row }">
              <el-input v-model="row.step_name" size="small" />
            </template>
          </el-table-column>
          <el-table-column prop="role" label="역할" width="100">
            <template #default="{ row }">
              <el-input v-model="row.role" size="small" />
            </template>
          </el-table-column>
          <el-table-column label="결재자">
            <template #default="{ row }">
              <el-select
                v-model="row.approver"
                filterable
                remote
                :remote-method="searchEmployees"
                placeholder="결재자 선택"
                size="small"
                style="width: 100%;"
              >
                <el-option
                  v-for="emp in availableEmployees"
                  :key="emp.id"
                  :label="formatEmployeeLabel(emp)"
                  :value="emp.id"
                />
              </el-select>
              <span v-if="row.approver_name" class="approver-hint">{{ row.approver_name }}</span>
            </template>
          </el-table-column>
          <el-table-column width="50" align="center">
            <template #default="{ $index }">
              <el-button type="danger" size="small" circle @click="removeStep('approvalSteps', $index)">
                <el-icon><Delete /></el-icon>
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-button type="primary" size="small" plain @click="addStep('approvalSteps', '결재')">
          + 결재 단계 추가
        </el-button>
      </template>

      <!-- 합의선 (AGREEMENT) -->
      <el-divider content-position="left">합의선 <span class="section-optional">(선택)</span></el-divider>

      <el-table v-if="form.agreementSteps.length" :data="form.agreementSteps" border size="small" style="margin-bottom: 10px;">
        <el-table-column prop="sequence" label="순서" width="60" align="center" />
        <el-table-column prop="step_name" label="단계명" width="120">
          <template #default="{ row }">
            <el-input v-model="row.step_name" size="small" />
          </template>
        </el-table-column>
        <el-table-column prop="role" label="역할" width="100">
          <template #default="{ row }">
            <el-input v-model="row.role" size="small" />
          </template>
        </el-table-column>
        <el-table-column label="합의자">
          <template #default="{ row }">
            <el-select
              v-model="row.approver"
              filterable
              remote
              :remote-method="searchEmployees"
              placeholder="합의자 선택"
              size="small"
              style="width: 100%;"
            >
              <el-option
                v-for="emp in availableEmployees"
                :key="emp.id"
                :label="formatEmployeeLabel(emp)"
                :value="emp.id"
              />
            </el-select>
            <span v-if="row.approver_name" class="approver-hint">{{ row.approver_name }}</span>
          </template>
        </el-table-column>
        <el-table-column width="50" align="center">
          <template #default="{ $index }">
            <el-button type="danger" size="small" circle @click="removeStep('agreementSteps', $index)">
              <el-icon><Delete /></el-icon>
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-button size="small" plain @click="addStep('agreementSteps', '합의')">
        + 합의 단계 추가
      </el-button>

      <!-- 참조선 (REFERENCE) -->
      <el-divider content-position="left">참조선 <span class="section-optional">(선택)</span></el-divider>

      <el-table v-if="form.referenceSteps.length" :data="form.referenceSteps" border size="small" style="margin-bottom: 10px;">
        <el-table-column prop="sequence" label="순서" width="60" align="center" />
        <el-table-column label="참조자">
          <template #default="{ row }">
            <el-select
              v-model="row.approver"
              filterable
              remote
              :remote-method="searchEmployees"
              placeholder="참조자 선택"
              size="small"
              style="width: 100%;"
            >
              <el-option
                v-for="emp in availableEmployees"
                :key="emp.id"
                :label="formatEmployeeLabel(emp)"
                :value="emp.id"
              />
            </el-select>
            <span v-if="row.approver_name" class="approver-hint">{{ row.approver_name }}</span>
          </template>
        </el-table-column>
        <el-table-column width="50" align="center">
          <template #default="{ $index }">
            <el-button type="danger" size="small" circle @click="removeStep('referenceSteps', $index)">
              <el-icon><Delete /></el-icon>
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-button size="small" plain @click="addStep('referenceSteps', '참조')">
        + 참조 추가
      </el-button>
    </el-form>

    <template #footer>
      <el-button @click="visible = false">취소</el-button>
      <el-button
        type="primary"
        @click="handleSubmit"
        :loading="submitting"
        :disabled="!canSubmit"
      >
        결재 요청
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { Delete, Loading } from '@element-plus/icons-vue'
import axiosInstance from '@/api/axios'

const props = defineProps({
  modelValue: Boolean,
  documentType: { type: String, required: true },
  // 로딩 전 undefined 허용 (2026-08-03) — 최종보고 미생성 시 comparison?.id 가
  // undefined 로 내려와 Vue prop 경고. 사용처는 documentId 존재 시에만 API 호출.
  documentId: { type: [String, Number], default: null },
  documentApiPath: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue', 'submitted'])

const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

const submitting = ref(false)
const loadingApprovers = ref(false)
const availableEmployees = ref([])

const form = reactive({
  title: '',
  memo: '',
  approvalSteps: [],
  agreementSteps: [],
  referenceSteps: [],
})

const canSubmit = computed(() => {
  return form.approvalSteps.length > 0 && form.approvalSteps.every(s => s.approver)
})

const formatEmployeeLabel = (emp) => {
  const name = emp.get_full_name || emp.username
  return name + (emp.department_name ? ` (${emp.department_name})` : '')
}

const loadSuggestedApprovers = async () => {
  loadingApprovers.value = true
  try {
    let url = 'approvals/suggested-approvers/'
    if (props.documentApiPath && props.documentId) {
      url = `${props.documentApiPath}/${props.documentId}/suggested-approvers/`
    }
    const response = await axiosInstance.get(url, {
      params: { document_type: props.documentType }
    })

    const data = response.data

    // 그룹 응답 처리 (신규) vs 배열 응답 (레거시 호환)
    if (data.approval) {
      form.approvalSteps = data.approval.map((step, i) => ({
        sequence: step.sequence || i + 1,
        step_name: step.step_name,
        role: step.role,
        approver: step.approver,
        approver_name: step.approver_name,
      }))
      form.agreementSteps = (data.agreement || []).map((step, i) => ({
        sequence: step.sequence || i + 1,
        step_name: step.step_name,
        role: step.role,
        approver: step.approver,
        approver_name: step.approver_name,
      }))
      form.referenceSteps = (data.reference || []).map((step, i) => ({
        sequence: i + 1,
        approver: step.approver,
        approver_name: step.approver_name,
      }))
    } else if (Array.isArray(data)) {
      // 레거시 호환: 배열이면 모두 결재선으로
      form.approvalSteps = data.map(step => ({
        sequence: step.sequence,
        step_name: step.step_name,
        role: step.role,
        approver: step.approver,
        approver_name: step.approver_name,
      }))
      form.agreementSteps = []
      form.referenceSteps = []
    }

    await searchEmployees('')
  } catch (error) {
    console.error('Failed to load suggested approvers:', error)
    form.approvalSteps = []
    form.agreementSteps = []
    form.referenceSteps = []
  } finally {
    loadingApprovers.value = false
  }
}

const searchEmployees = async (query) => {
  try {
    const response = await axiosInstance.get('employees/', {
      params: { search: query || '', page_size: 50 }
    })
    availableEmployees.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to search employees:', error)
  }
}

const addStep = (listName, defaultName) => {
  const list = form[listName]
  const nextSeq = list.length > 0 ? Math.max(...list.map(s => s.sequence)) + 1 : 1
  list.push({
    sequence: nextSeq,
    step_name: listName === 'referenceSteps' ? undefined : `${defaultName} ${nextSeq}`,
    role: '',
    approver: null,
    approver_name: '',
  })
}

const removeStep = (listName, index) => {
  form[listName].splice(index, 1)
  form[listName].forEach((step, i) => { step.sequence = i + 1 })
}

const handleSubmit = async () => {
  submitting.value = true
  try {
    // 3개 배열을 step_type 포함하여 병합
    const allSteps = [
      ...form.approvalSteps.map((s, i) => ({
        sequence: i + 1,
        step_name: s.step_name,
        role: s.role,
        step_type: 'APPROVAL',
        approver: s.approver,
      })),
      ...form.agreementSteps.map((s, i) => ({
        sequence: 100 + i + 1,
        step_name: s.step_name,
        role: s.role,
        step_type: 'AGREEMENT',
        approver: s.approver,
      })),
      ...form.referenceSteps.filter(s => s.approver).map((s, i) => ({
        sequence: 200 + i + 1,
        step_name: '참조',
        role: '',
        step_type: 'REFERENCE',
        approver: s.approver,
      })),
    ]

    if (props.documentApiPath && props.documentId) {
      await axiosInstance.post(`${props.documentApiPath}/${props.documentId}/submit-approval/`, {
        approval_steps: allSteps,
      })
    } else {
      await axiosInstance.post('approvals/submit/', {
        document_type: props.documentType,
        document_id: props.documentId,
        title: form.title || undefined,
        memo: form.memo,
        steps: allSteps,
      })
    }

    ElMessage.success('결재가 요청되었습니다.')
    visible.value = false
    emit('submitted')
  } catch (error) {
    console.error('Submit approval failed:', error)
    ElMessage.error(error.response?.data?.error || '결재 요청 중 오류가 발생했습니다.')
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.approver-hint {
  font-size: 12px;
  color: #909399;
}
.section-optional {
  font-size: 12px;
  color: #909399;
  font-weight: normal;
}
</style>
